import React, { useState, useMemo, useCallback, useEffect, useRef } from "react";
import {
  Box,
  Paper,
  Typography,
  Button,
  Tabs,
  Tab,
  IconButton,
  LinearProgress,
  CircularProgress,
  Chip,
  Snackbar,
  Alert,
  Menu,
  MenuItem,
  ListItemText,
  ListItemIcon,
} from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import FilterListIcon from "@mui/icons-material/FilterList";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import TrendingDownIcon from "@mui/icons-material/TrendingDown";
import AddIcon from "@mui/icons-material/Add";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import PlayCircleOutlineIcon from "@mui/icons-material/PlayCircleOutline";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CloseIcon from "@mui/icons-material/Close"; // Add this import
import InsightsImageIcon from "../../../assets/insights.png";
import GlobalFilterModal from "./GlobalFilterModal";
import InsightsView, { fetchInsightsByIds, TILE_ID_TO_NAME, TILE_NAME_TO_ID } from "./InsightsView";
import PinnedInsightsSection from "./PinnedInsightsSections";
import { useDispatch, useSelector } from "react-redux";
import DownloadOutlinedIcon from "@mui/icons-material/DownloadOutlined"; 
import {
  addTilePinOptimistic,
  fetchWorkflowDashboard,
  removeTilePinOptimistic,
  resetDashboardFilters,
  setDashboardFilters,
  toggleTilePin,
} from "./redux/WorkflowDashboardSlicer";
import { getSelectedAuditedFilesMenuData } from "../SelectFilesMenu/SelectFilesMenuSlice";
import { mapFiltersToPayload } from "./utils/mapDashboardFilters";
import SelectDashboardFileMenu from "./SelectFileDashboardMenu";
import html2canvas from "html2canvas";

const BORDER = "1px solid #E2E8F0";
const TEXT_MUTED = "#64748B";
const TEXT_HEADING = "#1E293B";
const BRAND_BLUE = "#1565C0";
const SURFACE = "#F8FAFC";

// Helper function to truncate file names
const truncateFileName = (name, maxLength = 25) => {
  if (!name) return "";
  if (name.length <= maxLength) return name;
  return name.substring(0, maxLength) + "...";
};

// ==================== StatCard Component ====================
function StatCard({ label, value, valueColor, trend, footnote, action }) {
  return (
    <Paper
      elevation={0}
      sx={{
        flex: 1,
        p: 2,
        border: BORDER,
        borderRadius: 2,
        display: "flex",
        flexDirection: "column",
        gap: 0.5,
        minWidth: 0,
      }}
    >
      <Typography sx={{ fontSize: 12.5, color: TEXT_MUTED, fontWeight: 500 }}>
        {label}
      </Typography>

      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <Typography
          sx={{
            fontSize: 20,
            fontWeight: 600,
            color: valueColor || TEXT_HEADING,
            lineHeight: 1.2,
          }}
        >
          {value}
        </Typography>

        {/* {trend && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.25,
              color: trend.direction === "up" ? "#16A34A" : "#DC2626",
              fontSize: 13,
              fontWeight: 600,
            }}
          >
            {trend.direction === "up" ? (
              <TrendingUpIcon sx={{ fontSize: 16 }} />
            ) : (
              <TrendingDownIcon sx={{ fontSize: 16 }} />
            )}
            {trend.label}
          </Box>
        )} */}

        {action && (
          <IconButton
            size="small"
            sx={{ ml: "auto", p: 0.25 }}
            onClick={action.onClick}
          >
            <NorthEastIcon sx={{ fontSize: 16, color: TEXT_MUTED }} />
          </IconButton>
        )}
      </Box>

      {footnote && (
        <Typography
          sx={{ fontSize: 11.5, color: TEXT_MUTED, marginTop: "auto" }}
        >
          {footnote}
        </Typography>
      )}
    </Paper>
  );
}

// ==================== AuditedEpisodesCard Component ====================
function AuditedEpisodesCard({
  completed,
  total,
  percent,
  weeklyVarianceLabel,
}) {
  // add — detect a negative value from the label itself 
  const isNegative =
    typeof weeklyVarianceLabel === "string" && weeklyVarianceLabel.trim().startsWith("-");

  return (
    <Paper
      elevation={0}
      sx={{
        flex: 1,
        p: 2,
        border: BORDER,
        borderRadius: 2,
        display: "flex",
        flexDirection: "column",
        gap: 0.75,
        minWidth: 0,
      }}
    >
      <Typography sx={{ fontSize: 12, color: TEXT_MUTED, fontWeight: 500 }}>
        Audited Episodes
      </Typography>

      <Typography
        sx={{
          fontSize: 20,
          fontWeight: 600,
          color: TEXT_HEADING,
          lineHeight: 1.2,
        }}
      >
        {completed} / {total}
      </Typography>

      <LinearProgress
        variant="determinate"
        value={percent}
        sx={{
          height: 6,
          borderRadius: 3,
          bgcolor: "#EEF2F6",
          "& .MuiLinearProgress-bar": { bgcolor: BRAND_BLUE, borderRadius: 3 },
        }}
      />

      <Box sx={{ display: "flex", justifyContent: "space-between" }}>
        <Typography sx={{ fontSize: 11.5, color: TEXT_MUTED }}>
          {percent}% Completed
        </Typography>
        <Typography
          sx={{
            fontSize: 11.5,
            color: isNegative ? "#DC2626" : "#16A34A", // changed — was hardcoded green
            fontWeight: 600,
          }}
        >
          {weeklyVarianceLabel}
        </Typography>
      </Box>
    </Paper>
  );
}
// ==================== DonutChart Component ====================
function DonutChart({ segments, total, centerLabel, size = 150 }) {
  const strokeWidth = size * (20 / 150);
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;

  let cumulative = 0;

  return (
    <Box
      sx={{ position: "relative", width: size, height: size, flexShrink: 0 }}
    >
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        <g transform={`rotate(-90 ${size / 2} ${size / 2})`}>
          {segments.map((seg) => {
            const fraction = seg.value / total;
            const dash = fraction * circumference;
            const gap = circumference - dash;
            const offset = -cumulative * circumference;
            cumulative += fraction;

            return (
              <circle
                key={seg.label}
                cx={size / 2}
                cy={size / 2}
                r={radius}
                fill="none"
                stroke={seg.color}
                strokeWidth={strokeWidth}
                strokeDasharray={`${dash} ${gap}`}
                strokeDashoffset={offset}
              />
            );
          })}
        </g>
      </svg>

      <Box
        sx={{
          position: "absolute",
          inset: 0,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Typography sx={{ fontSize: 20, fontWeight: 700, color: TEXT_HEADING }}>
          {centerLabel.value}
        </Typography>
        <Typography
          sx={{
            fontSize: 11,
            color: TEXT_MUTED,
            whiteSpace: "pre-line",
            textAlign: "center",
            lineHeight: 1.3,
          }}
        >
          {centerLabel.label}
        </Typography>
      </Box>
    </Box>
  );
}

// ==================== EpisodeStatusBreakdown Component ====================

function EpisodeStatusBreakdown({ segments, total }) {
  const captureRef = useRef(null); 
  const [menuAnchor, setMenuAnchor] = useState(null); 
  const [downloading, setDownloading] = useState(false); 

  const handleDownload = async () => { 
    setMenuAnchor(null);
    const el = captureRef.current;
    if (!el || downloading) return;

    setDownloading(true);
    try {
      const canvas = await html2canvas(el, {
        backgroundColor: "#ffffff",
        scale: 2,
        useCORS: true,
      });
      const dataUrl = canvas.toDataURL("image/jpeg", 0.95);
      const link = document.createElement("a");
      link.href = dataUrl;
      link.download = `episode-status-breakdown-${Date.now()}.jpeg`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (err) {
      console.error("Failed to download Episode Status Breakdown as JPEG:", err);
    } finally {
      setDownloading(false);
    }
  };

  return (
    <Paper
      ref={captureRef} // add — this is what gets captured
      elevation={0}
      sx={{
        flex: 1,
        p: { xs: 2, sm: 2.5 },
        border: BORDER,
        borderRadius: 2,
        minWidth: 0,
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 2,
        }}
      >
        <Typography
          sx={{
            fontSize: { xs: 16, sm: 20 },
            fontWeight: 600,
            color: TEXT_HEADING,
          }}
        >
          Episode Status Breakdown
        </Typography>

        <IconButton size="small" onClick={(e) => setMenuAnchor(e.currentTarget)}>
          <MoreVertIcon sx={{ fontSize: 18, color: TEXT_MUTED }} />
        </IconButton>
        <Menu
          anchorEl={menuAnchor}
          open={Boolean(menuAnchor)}
          onClose={() => setMenuAnchor(null)}
        >
          <MenuItem onClick={handleDownload} disabled={downloading}>
            <ListItemIcon>
              <DownloadOutlinedIcon sx={{ fontSize: 18 }} />
            </ListItemIcon>
            <ListItemText>{downloading ? "Downloading…" : "Download"}</ListItemText>
          </MenuItem>
        </Menu>
      </Box>

      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          alignItems: { xs: "center", sm: "center" },
          gap: { xs: 2.5, sm: 4 },
        }}
      >
        <DonutChart
          segments={segments}
          total={total}
          centerLabel={{ value: total, label: "Total" }}
          size={140}
        />

        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 1.25,
            flex: 1,
            width: "100%",
          }}
        >
          {segments.map((seg) => (
            <Box
              key={seg.label}
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Box
                  sx={{
                    width: 8,
                    height: 8,
                    borderRadius: "50%",
                    bgcolor: seg.color,
                    flexShrink: 0,
                  }}
                />
                <Typography sx={{ fontSize: 13, color: TEXT_HEADING }}>
                  {seg.label}
                </Typography>
              </Box>
              <Typography
                sx={{ fontSize: 13, fontWeight: 700, color: TEXT_HEADING }}
              >
                {seg.value}
              </Typography>
            </Box>
          ))}
        </Box>
      </Box>
    </Paper>
  );
}

// ==================== FinancialImpact Component ====================
function FinancialImpact({ data, range, onRangeChange }) {
  const captureRef = useRef(null);
  const [menuAnchor, setMenuAnchor] = useState(null); 
  const [downloading, setDownloading] = useState(false); 

  const active = data[range];

  const donutTotal = active.codingRevenue + active.cdqRevenue;

  const segments = [
    { label: "Coding Revenue", value: active.codingRevenue, color: BRAND_BLUE },
    { label: "CDQ Revenue", value: active.cdqRevenue, color: "#94A3B8" },
  ];

  const formatCompact = (num) => {
    if (num >= 1_000_000) return `$${(num / 1_000_000).toFixed(2)}M`;
    if (num >= 1_000) return `$${Math.round(num / 1000)}K`;
    return `$${num}`;
  };

  const handleDownload = async () => {
    setMenuAnchor(null);
    const el = captureRef.current;
    if (!el || downloading) return;

    setDownloading(true);
    try {
      const canvas = await html2canvas(el, {
        backgroundColor: "#ffffff",
        scale: 2,
        useCORS: true,
      });
      const dataUrl = canvas.toDataURL("image/jpeg", 0.95);
      const link = document.createElement("a");
      link.href = dataUrl;
      link.download = `financial-impact-${Date.now()}.jpeg`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (err) {
      console.error("Failed to download Financial Impact as JPEG:", err);
    } finally {
      setDownloading(false);
    }
  };

  return (
    <Paper
      ref={captureRef} 
      elevation={0}
      sx={{
        flex: 1,
        p: { xs: 2, sm: 2.5 },
        border: BORDER,
        borderRadius: 2,
        minWidth: 0,
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          justifyContent: "space-between",
          alignItems: { xs: "flex-start", sm: "center" },
          gap: 1.5,
          mb: 1.5,
        }}
      >
        <Typography
          sx={{
            fontSize: { xs: 15, sm: 16 },
            fontWeight: 700,
            color: TEXT_HEADING,
          }}
        >
          Financial Impact
        </Typography>

        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}> 
          <Box
            sx={{
              display: "flex",
              border: BORDER,
              borderRadius: 1.5,
              overflow: "hidden",
            }}
          >
            {["monthly", "total"].map((key) => (
              <Button
                key={key}
                size="small"
                onClick={() => onRangeChange(key)}
                sx={{
                  textTransform: "none",
                  fontSize: 12,
                  fontWeight: 600,
                  px: 1.5,
                  borderRadius: 0,
                  color: range === key ? "#fff" : TEXT_MUTED,
                  bgcolor: range === key ? BRAND_BLUE : "transparent",
                  "&:hover": { bgcolor: range === key ? BRAND_BLUE : SURFACE },
                }}
              >
                {key === "monthly" ? "Monthly" : "Total"}
              </Button>
            ))}
          </Box>

          <IconButton size="small" onClick={(e) => setMenuAnchor(e.currentTarget)}>
            <MoreVertIcon sx={{ fontSize: 18, color: TEXT_MUTED }} />
          </IconButton>

          <Menu
            anchorEl={menuAnchor}
            open={Boolean(menuAnchor)}
            onClose={() => setMenuAnchor(null)}
          >
            <MenuItem onClick={handleDownload} disabled={downloading}>
              <ListItemIcon>
                <DownloadOutlinedIcon sx={{ fontSize: 18 }} />
              </ListItemIcon>
              <ListItemText>{downloading ? "Downloading…" : "Download"}</ListItemText>
            </MenuItem>
          </Menu>
        </Box>
      </Box>

      <Typography
        sx={{
          fontSize: 11,
          fontWeight: 600,
          color: TEXT_MUTED,
          letterSpacing: 0.5,
          mb: 0.5,
        }}
      >
        {range === "monthly"
          ? "MONTHLY CONFIRMED REVENUE"
          : "TOTAL CONFIRMED REVENUE"}
      </Typography>
      <Typography
        sx={{ fontSize: 26, fontWeight: 700, color: TEXT_HEADING, mb: 2.5 }}
      >
        ${active.confirmedRevenue.toLocaleString()}
      </Typography>

      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          alignItems: "center",
          gap: { xs: 2.5, sm: 3 },
        }}
      >
        <DonutChart
          segments={segments}
          total={donutTotal}
          centerLabel={{
            value: formatCompact(active.confirmedRevenue),
            label: "TOTAL CONFIRMED\nREVENUE",
          }}
          size={140}
        />

        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 1,
            flex: 1,
            width: "100%",
          }}
        >
          {segments.map((seg) => (
            <Box
              key={seg.label}
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                bgcolor: SURFACE,
                borderRadius: 1.5,
                px: 1.5,
                py: 1.25,
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Box
                  sx={{
                    width: 10,
                    height: 10,
                    borderRadius: 0.5,
                    bgcolor: seg.color,
                    flexShrink: 0,
                  }}
                />
                <Typography sx={{ fontSize: 13, color: TEXT_HEADING }}>
                  {seg.label}
                </Typography>
              </Box>
              <Typography
                sx={{ fontSize: 13, fontWeight: 700, color: TEXT_HEADING }}
              >
                ${seg.value.toLocaleString()}
              </Typography>
            </Box>
          ))}
        </Box>
      </Box>
    </Paper>
  );
}

// ==================== MyInsightsEmptyState Component ====================
function MyInsightsEmptyState({ onAddInsight }) {
  return (
    <Paper
      elevation={0}
      sx={{
        p: { xs: 3, sm: 5 },
        border: BORDER,
        borderRadius: 2,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        textAlign: "center",
      }}
    >
      <Box
        sx={{
          width: 56,
          height: 56,
          borderRadius: "50%",
          bgcolor: SURFACE,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          mb: 2,
        }}
      >
        <Box
          component={"img"}
          src={InsightsImageIcon}
          alt="InsightsImageIcon"
        />
      </Box>

      <Box sx={{ mb: 1.5 }}>
  <Typography sx={{ fontSize: 16, fontWeight: 700, color: TEXT_HEADING }}>
    Personalized Dashboard
  </Typography>
  <Typography sx={{ mb:1.5,fontSize: 12.5, color: TEXT_MUTED }}>
    Analytics you've added from the Insights tab.
  </Typography>
</Box>

      <Typography
        sx={{ fontSize: 13, color: TEXT_MUTED, maxWidth: 420, mb: 2.5 }}
      >
        Personalize your dashboard by adding analytics that matter to your
        workflow. Track specific KPIs, regional metrics, or individual coder
        performance.
      </Typography>

      <Button
        variant="contained"
        startIcon={<AddIcon />}
        onClick={onAddInsight}
        sx={{
          textTransform: "none",
          fontWeight: 600,
          bgcolor: BRAND_BLUE,
          boxShadow: "none",
          "&:hover": { bgcolor: "#0D47A1", boxShadow: "none" },
        }}
      >
        Add Insight
      </Button>
    </Paper>
  );
}

// ==================== MAIN WorkflowDashboard Component ====================
export default function WorkflowDashboard({ activeModule, onModuleChange }) {
  const [tab, setTab] = useState("dashboard");
  const [financialRange, setFinancialRange] = useState("total");
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [selectFileAnchor, setSelectFileAnchor] = useState(null);
  const [pinToast, setPinToast] = useState(null);
  const [selectedFileId, setSelectedFileId] = useState(null);
  const [dashboardGenerated, setDashboardGenerated] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
const [searchTerm, setSearchTerm] = useState("");
  
  const dispatch = useDispatch();
  const { data, loading, filters } = useSelector(
    (state) => state.workflowDashboard,
  );
  const {
    data: filesList,
    status: filesStatus,
    hasMore,
  } = useSelector((state) => state.selectedAuditedFiles.files);
const pinnedInsightIds = useMemo(() => {
  const tiles = data?.assigned_tiles || [];
  return tiles.map((name) => TILE_NAME_TO_ID[name]).filter(Boolean);
}, [data]);
const pinnedInsightIdsKey = pinnedInsightIds.join(",");
  // Map files to the format expected by SelectDashboardFileMenu
  const mappedFiles = useMemo(() => {
    if (!filesList || !Array.isArray(filesList)) return [];
    return filesList.map((file) => {
      let fileName = "";
      if (file.file_name) {
        if (Array.isArray(file.file_name)) {
          fileName = file.file_name[0] || "";
        } else {
          fileName = file.file_name;
        }
      } else if (file.fileName) {
        if (Array.isArray(file.fileName)) {
          fileName = file.fileName[0] || "";
        } else {
          fileName = file.fileName;
        }
      } else if (file.name) {
        fileName = file.name;
      }

      return {
        id: file.header_id || file.id,
        header_id: file.header_id,
        file_name: file.file_name || file.fileName || file.name || "",
        fileName: fileName,
        load_id: file.load_id || file.loadId || "",
        start_date: file.start_date,
        end_date: file.end_date,
        created_at: file.created_at,
        ...file,
      };
    });
  }, [filesList]);

  // Find selected file - check both id and header_id with type conversion
  const selectedFile = mappedFiles.find((f) => {
    const fileId = String(f.id);
    const fileHeaderId = String(f.header_id);
    const selectedId = String(selectedFileId);
    return fileId === selectedId || fileHeaderId === selectedId;
  });


const fetchFiles = useCallback(
  (page = 1, search = searchTerm) => {
    dispatch(getSelectedAuditedFilesMenuData({ pageNumber: page, search }));
  },
  [dispatch, searchTerm],
);

const handleLoadMore = useCallback(() => {
  if (filesStatus === "LOADING" || !hasMore) return;
  const nextPage = currentPage + 1;
  setCurrentPage(nextPage);
  fetchFiles(nextPage, searchTerm);
}, [filesStatus, hasMore, currentPage, fetchFiles, searchTerm]);

const handleOpenFileMenu = useCallback(
  (e) => {
    setSelectFileAnchor(e.currentTarget);
    setCurrentPage(1);
    setSearchTerm("");
    fetchFiles(1, "");
  },
  [fetchFiles],
);

const handleFileSearch = useCallback(
  (search) => {
    setSearchTerm(search);
    setCurrentPage(1);
    dispatch(getSelectedAuditedFilesMenuData({ pageNumber: 1, search }));
  },
  [dispatch],
);

  // Handle clearing the selected file
  const handleClearSelectedFile = useCallback((e) => {
    e.stopPropagation(); // Prevent the dropdown from opening
    setSelectedFileId(null);
    setDashboardGenerated(false);
  }, []);

  const handleRunDashboard = () => {
    console.log("=== handleRunDashboard START ===");
    console.log("selectedFileId:", selectedFileId);
    console.log("selectedFile:", selectedFile);

    if (!selectedFileId) {
      console.log("ERROR: No file selected (selectedFileId is null)");
      return;
    }

    if (!selectedFile) {
      console.log("ERROR: selectedFile not found in mappedFiles");
      console.log("Looking for file with id:", selectedFileId);
      console.log(
        "Available files:",
        mappedFiles.map((f) => ({
          id: f.id,
          header_id: f.header_id,
          name: f.fileName,
        })),
      );
      return;
    }

    console.log("Selected file name:", selectedFile.fileName);

    // Create payload directly
    const payload = {
      ...filters,
      file_name: [selectedFile.fileName],
    };

    console.log("Payload being sent:", JSON.stringify(payload, null, 2));

    try {
      dispatch(setDashboardFilters(payload));
      console.log("✅ setDashboardFilters dispatched");

      dispatch(fetchWorkflowDashboard(payload));
      console.log("✅ fetchWorkflowDashboard dispatched");

      setDashboardGenerated(true);
      console.log("✅ dashboardGenerated set to true");
    } catch (error) {
      console.error("❌ Error in handleRunDashboard:", error);
    }

    console.log("=== handleRunDashboard END ===");
  };
  const handleFinancialRangeChange = (range) => {
    setFinancialRange(range);

    const payload = {
      ...filters,
      financial_impact: range === "monthly" ? "monthly" : "", // "" for Total = normal/default
    };

    dispatch(setDashboardFilters(payload));
    dispatch(fetchWorkflowDashboard(payload));
  };

  const handleFileApply = (fileId) => {
    console.log("File selected from menu:", fileId);
    setSelectedFileId(fileId);
    setDashboardGenerated(false);
    setSelectFileAnchor(null);
  };

  // Get file name for display
  const getDisplayFileName = (file) => {
    if (!file) return "";
    return file.fileName || "";
  };

  const togglePinInsight = async (id) => {
    const tileName = TILE_ID_TO_NAME[id];
    if (!tileName) return;

    const isPinned = pinnedInsightIds.includes(id);
    const pin = !isPinned;

    // optimistic — UI updates immediately, reverted on failure
    dispatch(
      pin ? addTilePinOptimistic(tileName) : removeTilePinOptimistic(tileName),
    );

    const result = await dispatch(toggleTilePin({ tileName, pin }));

    if (toggleTilePin.fulfilled.match(result)) {
      setPinToast({
        message: pin
          ? `"${tileName}" added to My Dashboard`
          : `"${tileName}" removed from My Dashboard`,
        severity: "success",
      });
    } else {
      setPinToast({
        message: `Couldn't ${pin ? "pin" : "unpin"} "${tileName}". Please try again.`,
        severity: "error",
      });
    }
  };

  const STATUS_COLORS = {
    "flagged for review": "#00488D",
    "auditing in progress": "#575E70",
    "reviewed no change": "#D9DFF5",
    "reviewed query sent": "#A04401",
    "reviewed recorded": "#C2C6D4",
  };

  const normalizeStatusKey = (status) =>
    (status || "")
      .toLowerCase()
      .replace(/[-–—]/g, "") // strip any dash variant
      .replace(/\s+/g, " ")
      .trim();

  const dashboardData = useMemo(() => {
    if (!data) return null;

    const auditedEpisodesRaw = data.audited_episodes || {};
    const fraction = auditedEpisodesRaw.fraction || "0/0";
    const [completedStr, totalStr] = fraction.split("/");
    const completed = Number(completedStr) || 0;
    const total = Number(totalStr) || 0;

    // Prefer the API's own percentage/variance strings; fall back to computing
    // percent locally only if the API ever omits them.
    const percentStr = auditedEpisodesRaw.percentage_completed || "";
    const percent = percentStr
      ? Math.round(parseFloat(percentStr))
      : total > 0
        ? Math.round((completed / total) * 100)
        : 0;

    const weeklyVarianceLabel =
      auditedEpisodesRaw.weekly_variance || "0% vs last week";

    const codingRevenue =
      data.financial_impact?.data?.find((d) => d.label === "Coding Revenue")
        ?.value || 0;
    const cdqRevenue =
      data.financial_impact?.data?.find((d) => d.label === "CDQ Revenue")
        ?.value || 0;
    const pipelineTotal = (data.episode_status_breakdown?.data || []).reduce(
      (sum, s) => sum + s.count,
      0,
    );
    return {
      auditedEpisodes: {
        completed,
        total,
        percent,
        weeklyVarianceLabel, // NEW — full label straight from the API
      },
      drgChanges: {
        value: data.drg_changes || 0,
        trend: { direction: "up", label: "+0" },
        footnote: "Total identified discrepancies",
      },
      drgErrorRate: {
        value: data.drg_error_rate || "0%",
        trend: { direction: "down", label: "-0%" },
        footnote: "Variance against coding standard",
      },
      episodeStatus: {
        total: pipelineTotal,
        segments: (data.episode_status_breakdown?.data || []).map((s) => ({
          label: s.status,
          value: s.count,
          color: STATUS_COLORS[normalizeStatusKey(s.status)] || "#94A3B8",
        })),
      },
      financialImpact: {
        total: {
          codingRevenue,
          cdqRevenue,
          confirmedRevenue: data.total_confirmed_revenue || 0,
        },
        monthly: {
          codingRevenue,
          cdqRevenue,
          confirmedRevenue: data.total_confirmed_revenue || 0,
        },
      },
    };
  }, [data]);

  const handleAddInsight = () => {};
useEffect(() => {
  if (!dashboardGenerated) return;
  if (tab !== "dashboard") return;
  if (pinnedInsightIds.length === 0) return;
  fetchInsightsByIds(dispatch, filters, pinnedInsightIds);
}, [tab, filters, pinnedInsightIdsKey, dashboardGenerated, dispatch]); 
  useEffect(() => {
    dispatch(resetDashboardFilters());
  }, []);
  return (
    <Box
      sx={{
        p: { xs: 1.5, sm: 2, md: 3 },
        bgcolor: "#F8fafc",
        minHeight: "100%",
        display: "flex",
        marginTop:"16px",
        borderRadius:"16px",
        flexDirection: "column",
        position: "relative",
      }}
    >
      {loading && dashboardGenerated && (
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            bgcolor: "rgba(255,255,255,0.6)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 10,
          }}
        >
          <CircularProgress />
        </Box>
      )}

      {/* Page header */}
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
          justifyContent: "space-between",
          alignItems: { xs: "stretch", md: "flex-start" },
          gap: 1.5,
          mb: 2,
        }}
      >
        <Box>
          <Typography
            sx={{
              fontSize: { xs: 17, sm: 20 },
              fontWeight: 700,
              color: TEXT_HEADING,
            }}
          >
            {tab === "insights" ? "Insights" : "My Dashboard"}
          </Typography>
          <Typography
            sx={{
              fontSize: { xs: 13, sm: 14 },
              color: "#64748B",
              fontFamily: "Inter",
            }}
          >
            {tab === "insights"
              ? "Explore coding, audit, DRG and financial summary statistics."
              : "Monitor coding, audit and quality metrics from your personalized dashboard."}
          </Typography>
        </Box>

        <Box
          sx={{
            display: "flex",
            gap: 1,
            flexWrap: "wrap",
            alignItems: "center",
          }}
        >
          {/* Select File Button with selected file display and clear button */}
          <Box
            sx={{
              position: "relative",
              display: "inline-flex",
              alignItems: "center",
            }}
          >
            <Button
              variant="outlined"
              endIcon={<KeyboardArrowDownIcon />}
              startIcon={
                selectedFile ? (
                  <CheckCircleIcon sx={{ fontSize: 16, color: "#16A34A" }} />
                ) : (
                  <DescriptionOutlinedIcon sx={{ fontSize: 16 }} />
                )
              }
              onClick={handleOpenFileMenu}
              sx={{
                textTransform: "none",
                fontWeight: 600,
                fontSize: 13,
                color: selectedFile ? "#1E293B" : "#334155",
                borderColor: selectedFile ? "#16A34A" : "#C1C6D4",
                backgroundColor: selectedFile ? "#F0FDF4" : "#FFFFFF",
                borderRadius: 2,
                whiteSpace: "nowrap",
                display: "flex",
                justifyContent: "start",
                minWidth: 150,
                maxWidth: 220,
                pr: selectedFile ? 4.5 : 1.5,
                overflow: "hidden",
                textOverflow: "ellipsis",
                "&:hover": {
                  borderColor: selectedFile ? "#16A34A" : "#C1C6D4",
                  backgroundColor: selectedFile ? "#F0FDF4" : "#FFFFFF",
                },
                "& .MuiButton-startIcon": {
                  marginRight: 0.5,
                },
              }}
              title={selectedFile ? getDisplayFileName(selectedFile) : ""}
            >
              {selectedFile
                ? truncateFileName(getDisplayFileName(selectedFile), 20)
                : "Select File"}
            </Button>

            {/* Clear/Remove button - only shown when a file is selected */}
            {selectedFile && (
              <IconButton
                size="small"
                onClick={handleClearSelectedFile}
                sx={{
                  position: "absolute",
                  right: 4,
                  top: "50%",
                  transform: "translateY(-50%)",
                  p: 0.25,
                  color: "#94A3B8",
                  "&:hover": {
                    color: "#DC2626",
                    backgroundColor: "transparent",
                  },
                }}
                title="Remove selected file"
              >
                <CloseIcon sx={{ fontSize: 16 }} />
              </IconButton>
            )}
          </Box>

          {/* Single File Selection Menu */}
          <SelectDashboardFileMenu
            anchorEl={selectFileAnchor}
            open={Boolean(selectFileAnchor)}
            onClose={() => setSelectFileAnchor(null)}
            files={mappedFiles}
            selectedFileId={selectedFileId}
            onApply={handleFileApply}
            loading={filesStatus === "LOADING" && currentPage === 1}
            loadingMore={filesStatus === "LOADING" && currentPage > 1}
            hasMore={hasMore}
            onLoadMore={handleLoadMore}
            onSearch={handleFileSearch}
          />

          {/* Run Dashboard Button */}
          <Button
            variant="contained"
            disabled={!selectedFileId || loading}
            startIcon={<PlayCircleOutlineIcon sx={{ fontSize: 16 }} />}
            onClick={handleRunDashboard}
            sx={{
              textTransform: "none",
              fontWeight: 600,
              fontSize: 13,
              bgcolor: "#1565C0",
              boxShadow: "none",
              borderRadius: 2,
              "&:hover": { bgcolor: "#0D47A1", boxShadow: "none" },
              "&.Mui-disabled": { bgcolor: "#E2E8F0", color: "#94A3B8" },
            }}
          >
            Run Dashboard
          </Button>

          {/* Filters Button - only enabled when dashboard is generated */}
          <Button
            variant="outlined"
            disabled={!dashboardGenerated}
            startIcon={
              <FilterListIcon sx={{ fontSize: 16, color: "#181C20" }} />
            }
            onClick={() => setFiltersOpen(true)}
            sx={{
              textTransform: "none",
              color: "#181C20",
              borderColor: "#C1C6D4",
              backgroundColor: "#FFFFFF",
              borderRadius: 2,
              fontSize: 13,
              fontWeight: 600,
              whiteSpace: "nowrap",
              "&:hover": {
                borderColor: "#C1C6D4",
                backgroundColor: "#FFFFFF",
              },
              "&.Mui-disabled": {
                backgroundColor: "#F1F5F9",
                color: "#94A3B8",
              },
            }}
          >
            Filters
          </Button>
        </Box>
      </Box>

      {/* Conditional rendering: Show empty state OR dashboard content */}
      {!dashboardGenerated ? (
        <Paper
          elevation={0}
          sx={{
            p: { xs: 4, sm: 6 },
            border: BORDER,
            borderRadius: 2,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            textAlign: "center",
          }}
        >
          <DescriptionOutlinedIcon
            sx={{ fontSize: 40, color: "#94A3B8", mb: 1.5 }}
          />
          <Typography
            sx={{ fontSize: 15, fontWeight: 700, color: TEXT_HEADING, mb: 0.5 }}
          >
            Select a file to get started
          </Typography>
          <Typography sx={{ fontSize: 13, color: TEXT_MUTED, maxWidth: 420 }}>
            Choose a file above, then click "Run Dashboard" to generate your
            workflow dashboard and insights.
          </Typography>
        </Paper>
      ) : (
        <>
          {/* Tabs - only shown when dashboard is generated */}
          <Paper
            elevation={0}
            sx={{
              mb: { xs: 2, sm: 3 },
              px: 2,
              pt: 1,
              bgcolor: "#fff",
              borderRadius: 2,
              border: "1px solid #E5E7EB",
              boxShadow: "0px 1px 7px #E4E4E7",
            }}
          >
            <Tabs
              value={tab}
              onChange={(_, v) => setTab(v)}
              variant="scrollable"
              scrollButtons={false}
              allowScrollButtonsMobile
              sx={{
                minHeight: 36,
                borderBottom: BORDER,
                "& .MuiTabs-indicator": { bgcolor: BRAND_BLUE },
              }}
            >
              <Tab
                value="dashboard"
                label="My Dashboard"
                sx={{
                  textTransform: "none",
                  fontSize: 13,
                  fontWeight: 600,
                  minHeight: 36,
                }}
              />
              <Tab
                value="insights"
                label="Insights"
                sx={{
                  textTransform: "none",
                  fontSize: 13,
                  fontWeight: 600,
                  minHeight: 36,
                }}
              />
            </Tabs>
          </Paper>

          {tab === "dashboard" && dashboardData && (
            <>
              {/* Stat cards */}
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: {
                    xs: "1fr",
                    sm: "repeat(2, 1fr)",
                    lg: "repeat(3, 1fr)",
                  },
                  gap: 2,
                  mb: 2,
                }}
              >
                <AuditedEpisodesCard {...dashboardData.auditedEpisodes} />
                <StatCard
                  label="DRG Changes"
                  value={dashboardData.drgChanges.value}
                  trend={dashboardData.drgChanges.trend}
                  footnote={dashboardData.drgChanges.footnote}
                />
                <StatCard
                  label="DRG Error Rate"
                  value={dashboardData.drgErrorRate.value}
                  trend={dashboardData.drgErrorRate.trend}
                  footnote={dashboardData.drgErrorRate.footnote}
                />
              </Box>

              {/* Donut + Financial impact */}
              <Box
                sx={{
                  display: "flex",
                  flexDirection: { xs: "column", lg: "row" },
                  gap: 2,
                  mb: 2,
                  alignItems: "stretch",
                }}
              >
                <EpisodeStatusBreakdown
                  segments={dashboardData.episodeStatus.segments}
                  total={dashboardData.episodeStatus.total}
                />
                <FinancialImpact
                  data={dashboardData.financialImpact}
                  range={financialRange}
                  onRangeChange={handleFinancialRangeChange}
                />
              </Box>

              {/* My Insights */}
              {pinnedInsightIds.length === 0 ? (
                <MyInsightsEmptyState onAddInsight={() => setTab("insights")} />
              ) : (
                <PinnedInsightsSection
                  pinnedIds={pinnedInsightIds}
                  onTogglePin={togglePinInsight}
                  onBrowseInsights={() => setTab("insights")} // add
                />
              )}
            </>
          )}

          {tab === "insights" && (
            <InsightsView
              pinnedIds={pinnedInsightIds}
              onTogglePin={togglePinInsight}
            />
          )}
        </>
      )}

      <GlobalFilterModal
        open={filtersOpen}
        onClose={() => setFiltersOpen(false)}
      />
      <Snackbar
        open={Boolean(pinToast)}
        autoHideDuration={3000}
        onClose={() => setPinToast(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setPinToast(null)}
          severity={pinToast?.severity || "success"}
          variant="filled"
          sx={{ width: "100%" }}
        >
          {pinToast?.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
